
import { useState, useEffect } from 'react';
import { ScriptBlock, AnalysisData, SceneItem, CharacterStat, LocationStat, PropStat, SfxStat, CameraStat, TransitionStat } from "@/types";
import {
    SHARED_IGNORE_LIST, SFX_KEYWORDS, SFX_SOUNDS, extractInlineSfx,
    isSfxToken,
    isGroupCharacter,
    COMMON_INANIMATE_OBJECTS,
    extractLocationsFromAction
} from "@/utils/scriptUtils";

const CHARS_PER_PAGE = 3000;

const canonicalizeName = (rawName: string): string => {
    // Remove parentheticals like (V.O.), (CONT'D), (O.S.), (OFF)
    let name = rawName.replace(/\s*\(.*?\)\s*/g, '');
    // Remove trailing special chars often left by bad formatting
    name = name.replace(/[:\-\.]+$/, '');
    return name.trim().toUpperCase();
};

// Common script terms and stopwords to ignore in Props detection (EN + PT/ES/FR)
// Local IGNORED_PROPS replaced by SHARED_IGNORE_LIST from scriptUtils

// Helper to detect if a block is actually Metadata/Analysis and should be ignored
const isMetadataBlock = (content: string): boolean => {
    const t = content.trim().toUpperCase();
    if (!t) return false;

    // 1. Check for Headers/Separators
    if (t.startsWith('###') || t.startsWith('***') || t.startsWith('---') || t.startsWith('___') || t.startsWith('===')) return true;

    // 2. Remove Markdown artifacts to check content
    // Replace leading **, *, #, -, > and whitespace
    const clean = t.replace(/^[\*\-\#\>\s]+/, '').replace(/[\*\-\#\<\s]+$/, '');

    // 3. Known Metadata Phrases (Exact or Start)
    const exactPhrases = [
        'METADADOS DO ROTEIRO', 'METADATA', 'ROTEIRO DE CINEMA', 'SCREENPLAY',
        'SCRIPT ANALYSIS', 'ANÁLISE DE ROTEIRO', 'STORY PILLARS', 'PILARES DA HISTÓRIA',
        'ANÁLISE SOB A ÓTICA'
    ];
    if (exactPhrases.some(p => clean === p || clean.startsWith(p + ':') || clean.startsWith(p + ' -'))) return true;

    // 4. Check for Key-Value pairs
    const metadataKeys = [
        'CLASSIFICAÇÃO', 'CLASSIFICATION',
        'ANÁLISE', 'ANALYSIS', 'PILARES', 'PILLARS',
        'PROTAGONISTA', 'PROTAGONIST', 'ANTAGONISTA', 'ANTAGONIST',
        'OBJETIVO', 'GOAL', 'CONFLITO', 'CONFLICT',
        'APOSTAS', 'STAKES', 'CENÁRIO', 'SETTING',
        'GÊNERO', 'GENRE', 'TOM', 'TONE', 'ORÇAMENTO', 'BUDGET',
        'LOGLINE', 'SINOPSE', 'SYNOPSIS', 'TEMA', 'THEME',
        'PLANT', 'PAYOFF', 'SETUP', 'RESOLUÇÃO', 'DATA', 'DATE', 'AUTOR', 'AUTHOR',
        'FRAMEWORK', 'METODOLOGIA'
    ];

    // Check strict start with colon (e.g. "GENRE: Horror") or exact match (header)
    const hasMetadataKey = metadataKeys.some(key =>
        clean.startsWith(key + ':') ||
        clean.startsWith(key + ' :') ||
        (clean === key)
    );

    return hasMetadataKey;
};

export type ElementCategory = 'CHARACTER' | 'LOCATION' | 'PROP' | 'SFX' | 'CAMERA' | 'TRANSITION' | string;

export const performScriptAnalysis = (
    blocks: ScriptBlock[],
    config: any = {},
    manualOverrides: Record<string, ElementCategory> = {},
    learnedElements: Record<string, string> = {}
): AnalysisData => {
    let totalStatsCharCount = 0;
    let totalStatsWordCount = 0;
    let foundFirstScene = false;

    const tempScenes: SceneItem[] = [];
    const tempChars: Record<string, CharacterStat> = {};
    const tempLocs: Record<string, LocationStat> = {};
    const tempProps: Record<string, PropStat> = {};
    const tempSfx: Record<string, SfxStat> = {};
    const tempCameras: Record<string, CameraStat> = {};
    const tempTransitions: Record<string, TransitionStat> = {};
    const tempCustom: Record<string, any[]> = {};

    const knownCharacters = new Set<string>();
    const knownLocations = new Set<string>();

    const getOverriddenCategory = (name: string, defaultCat: ElementCategory): ElementCategory => {
        const upName = name.toUpperCase();
        // 1. Priority: Manual Overrides (Project-Specific)
        if (manualOverrides && manualOverrides[upName]) {
            return manualOverrides[upName];
        }
        // 2. Priority: Learned Elements (Global Knowledge Base)
        if (learnedElements && learnedElements[upName]) {
            return learnedElements[upName];
        }
        // 3. Automated Heuristics
        return defaultCat;
    };

    const addToCategory = (name: string, category: ElementCategory, blockId: string, extra: any = {}, incrementCount: boolean = true) => {
        const upName = name.toUpperCase();
        const actualCategory = getOverriddenCategory(upName, category);
        const finalCategory = actualCategory === 'SFX' ? 'SFX_VFX' : actualCategory;

        if (actualCategory === 'IGNORE') return; // P0 priority: skip entirely

        // Prevent double counting: If remapped from CHARACTER to a non-character category, 
        // exclude dialogue blocks from count (the character cue already counts as the occurrence)
        if (category === 'CHARACTER' && actualCategory !== 'CHARACTER' && extra.isDialogue) {
            incrementCount = false;
        }

        if (actualCategory === 'CHARACTER') {
            if (!tempChars[upName]) {
                tempChars[upName] = { name: upName, dialogueCount: 0, sceneCount: 0, dialogueBlockIds: [], ...extra };
            }
            if (incrementCount && extra.dialogueCount) tempChars[upName].dialogueCount += extra.dialogueCount;
            if (!tempChars[upName].dialogueBlockIds.includes(blockId)) {
                if (extra.isDialogue) tempChars[upName].dialogueBlockIds.push(blockId);
            }
            if (extra.isIntroduced) tempChars[upName].isIntroduced = true;
            knownCharacters.add(upName);
        } else if (actualCategory === 'LOCATION') {
            if (!tempLocs[upName]) {
                tempLocs[upName] = { name: upName, count: 0, sceneBlockIds: [] };
            }
            if (incrementCount) tempLocs[upName].count++;
            if (!tempLocs[upName].sceneBlockIds.includes(blockId)) tempLocs[upName].sceneBlockIds.push(blockId);
            if (extra.description && !tempLocs[upName].description) tempLocs[upName].description = extra.description;
            knownLocations.add(upName);
        } else if (actualCategory === 'PROP') {
            if (!tempProps[upName]) {
                tempProps[upName] = { name: upName, count: 0, sceneBlockIds: [] };
            }
            if (incrementCount) tempProps[upName].count++;
            if (!tempProps[upName].sceneBlockIds.includes(blockId)) tempProps[upName].sceneBlockIds.push(blockId);
        } else if (actualCategory === 'SFX' || finalCategory === 'SFX_VFX') {
            if (!tempSfx[upName]) {
                tempSfx[upName] = { name: upName, count: 0, sceneBlockIds: [] };
            }
            if (incrementCount) tempSfx[upName].count++;
            if (!tempSfx[upName].sceneBlockIds.includes(blockId)) tempSfx[upName].sceneBlockIds.push(blockId);
        } else if (actualCategory === 'CAMERA') {
            if (!tempCameras[upName]) {
                tempCameras[upName] = { name: upName, count: 0, sceneBlockIds: [] };
            }
            if (incrementCount) tempCameras[upName].count++;
            if (!tempCameras[upName].sceneBlockIds.includes(blockId)) tempCameras[upName].sceneBlockIds.push(blockId);
        } else if (actualCategory === 'TRANSITION') {
            if (!tempTransitions[upName]) {
                tempTransitions[upName] = { name: upName, count: 0, sceneBlockIds: [] };
            }
            if (incrementCount) tempTransitions[upName].count++;
            if (!tempTransitions[upName].sceneBlockIds.includes(blockId)) tempTransitions[upName].sceneBlockIds.push(blockId);
        } else {
            // Custom Category
            if (!tempCustom[actualCategory]) {
                tempCustom[actualCategory] = [];
            }
            let item = tempCustom[actualCategory].find((i: any) => i.name === upName);
            if (!item) {
                item = { name: upName, count: 0, sceneBlockIds: [] };
                tempCustom[actualCategory].push(item);
            }
            if (incrementCount) item.count++;
            if (!item.sceneBlockIds.includes(blockId)) item.sceneBlockIds.push(blockId);
        }
    };

    const propVerbs = ['SEGURA', 'PEGA', 'COLOCA', 'CARREGA', 'USA', 'EMPUNHA', 'LEVANTA', 'GUARDA', 'ABRE', 'FECHA'];
    const locationPrepositions = [' EM ', ' NO ', ' NA ', ' NUM ', ' NUMA '];

    // PASS 1
    blocks.forEach(block => {
        if (block.type === 'scene-heading') foundFirstScene = true;
        if (!foundFirstScene) return;
        if (block.type === 'action' && ((block as any).metadata || isMetadataBlock(block.content))) return;

        if (block.type === 'character') {
            const canonName = canonicalizeName(block.content);
            if (canonName.length > 0 && !SHARED_IGNORE_LIST.has(canonName)) {
                addToCategory(canonName, 'CHARACTER', block.id, {}, false);
            }
        }
        if (block.type === 'scene-heading') {
            const locMatch = block.content.match(/^(?:INT\.|EXT\.|I\/E|INT |EXT |CENA|SCENE|EST\.|ESTAB\.)\s*(.+?)(?:\s-\s|$)/i);
            let locName = locMatch && locMatch[1] ? locMatch[1].trim().toUpperCase() : block.content.trim().toUpperCase();
            locName = locName.replace(/^\d+[\.\)\-]\s*/, '');
            if (locName.length > 0 && !SHARED_IGNORE_LIST.has(locName)) {
                addToCategory(locName, 'LOCATION', block.id, {}, false);
            }
        }
    });

    // PASS 2
    // PASS 2 - Physical Layout Simulation Logic
    const LINES_PER_PAGE = 55; // Average lines per A4 page in Courier 12pt
    const ACTION_WIDTH = 61;   // Standard action width (approx)
    const DIALOGUE_WIDTH = 31; // Standard dialogue column width
    const PARENTH_WIDTH = 25;  // Parenthetical width

    let totalPhysicalLines = 0;

    // Initial Offset: Title Page and Metadata Blocks
    // User images show ~4 lines for title/author + 4 lines for custom metadata (TITLE, PRODUCT, DURATION, TOM)
    // plus empty spaces, usually totaling ~15-20 lines before the first scene.
    totalPhysicalLines += 18;

    foundFirstScene = false;
    let runningCharCount = 0;

    blocks.forEach((block, index) => {
        if (block.type === 'scene-heading') foundFirstScene = true;

        runningCharCount += block.content.length;
        totalStatsWordCount += block.content.split(/\s+/).filter(Boolean).length;

        if (!foundFirstScene) return;

        const isMetadata = block.type === 'action' && ((block as any).metadata || isMetadataBlock(block.content));
        if (isMetadata) return;

        // PHYSICAL SIMULATION
        let blockLines = 0;
        const content = block.content.trim();
        if (content.length > 0) {
            switch (block.type) {
                case 'scene-heading':
                    blockLines = Math.ceil(content.length / ACTION_WIDTH) + 2; // +2 for double space before
                    break;
                case 'action':
                case 'sfx':
                case 'camera-direction':
                    blockLines = Math.ceil(content.length / ACTION_WIDTH) + 1; // +1 for space after
                    break;
                case 'transition':
                    blockLines = 2; // Transition line + space
                    break;
                case 'character':
                    blockLines = 1; // Character name line
                    break;
                case 'parenthetical':
                    blockLines = Math.ceil(content.length / PARENTH_WIDTH);
                    break;
                case 'dialogue':
                    blockLines = Math.ceil(content.length / DIALOGUE_WIDTH) + 1; // +1 for space after dialogue
                    break;
            }
        }
        totalPhysicalLines += blockLines;

        const currentPage = Math.floor(runningCharCount / CHARS_PER_PAGE) + 1;

        if (block.type === 'scene-heading') {
            const locMatch = block.content.match(/^(?:INT\.|EXT\.|I\/E|INT |EXT |CENA|SCENE|EST\.|ESTAB\.)\s*(.+?)(?:\s-\s|$)/i);
            let locName = locMatch && locMatch[1] ? locMatch[1].trim().toUpperCase() : block.content.trim().toUpperCase();
            locName = locName.replace(/^\d+[\.\)\-]\s*/, '');

            const nextBlock = blocks[index + 1];
            let desc = (nextBlock && nextBlock.type === 'action' && !isMetadataBlock(nextBlock.content)) ? nextBlock.content : undefined;
            addToCategory(locName, 'LOCATION', block.id, { description: desc });

            let isSlow = false;
            if (config?.methodField || (config?.methodMcKee && config?.methodField)) {
                if (currentPage > 12 && currentPage < 25) isSlow = true;
            }
            tempScenes.push({ id: block.id, slug: block.content, page: currentPage, isSlowPaced: isSlow });
        }

        if (block.type === 'character') {
            const canonName = canonicalizeName(block.content);
            if (canonName.length > 0) {
                const up = block.content.toUpperCase();
                const isNarrator = up.includes('NARRADOR') || up.includes('NARRATOR');
                const isOffScreen = up.includes('(V.O.)') || up.includes('(O.S.)') || up.includes('(OFF)');
                addToCategory(canonName, 'CHARACTER', block.id, { isNarrator, isOffScreen });
            }
        }

        if (block.type === 'dialogue') {
            let speakerName = null;
            for (let j = index - 1; j >= 0; j--) {
                if (blocks[j].type === 'character') {
                    speakerName = canonicalizeName(blocks[j].content);
                    break;
                }
                if (blocks[j].type === 'scene-heading' || blocks[j].type === 'transition' || (blocks[j].type === 'action' && blocks[j].content.trim() !== '')) break;
            }
            if (speakerName) addToCategory(speakerName, 'CHARACTER', block.id, { dialogueCount: 1, isDialogue: true });
        }

        if (block.type === 'action') {
            const overridesHandledThisBlock = new Set<string>();
            const contentUpper = block.content.toUpperCase();
            const introRegex = /\b([A-ZÀ-Ú][A-ZÀ-Ú0-9\.\-]*?(?:\s+[A-ZÀ-Ú0-9\.\-]+)*)\s*\(([^)]+)\)/g;
            let charMatch;
            const soundIndicators = ['SOM DE', 'SOUND OF', 'SFX', 'SONOROPLASTIA', 'BARULHO', 'EFEITO'];
            const honorableMentions = ['MR.', 'MRS.', 'MS.', 'DR.', 'SR.', 'JR.', 'SGT.', 'LT.', 'CAPT.', 'PROF.', 'ENG.'];

            while ((charMatch = introRegex.exec(block.content)) !== null) {
                const namePart = charMatch[1].trim();
                const parenContent = charMatch[2].toUpperCase();
                if (soundIndicators.some(ind => parenContent.includes(ind))) continue;
                const isHonorific = honorableMentions.some(h => namePart.toUpperCase().startsWith(h));
                if (namePart.endsWith('.') && !isHonorific) continue;
                const name = canonicalizeName(namePart);
                if (COMMON_INANIMATE_OBJECTS.includes(name.split(' ')[0]) || COMMON_INANIMATE_OBJECTS.includes(name)) continue;
                if (!SHARED_IGNORE_LIST.has(name)) {
                    addToCategory(name, 'CHARACTER', block.id, { isIntroduced: true });
                    if (manualOverrides[name]) overridesHandledThisBlock.add(name);
                }
            }

            const agentRegex = /\b(?:A|O|AS|OS|UM|UMA)\s+([A-ZÀ-Ú]{2,}(?:\s+[A-ZÀ-Ú]{2,})*)\b/g;
            let agentMatch;
            while ((agentMatch = agentRegex.exec(block.content)) !== null) {
                const name = canonicalizeName(agentMatch[1]);
                if (COMMON_INANIMATE_OBJECTS.includes(name.split(' ')[0]) || COMMON_INANIMATE_OBJECTS.includes(name)) continue;
                if (!SHARED_IGNORE_LIST.has(name) && !knownLocations.has(name)) {
                    addToCategory(name, 'CHARACTER', block.id, { isIntroduced: true });
                    if (manualOverrides[name] || learnedElements[name]) overridesHandledThisBlock.add(name);
                }
            }

            const capPhrases = block.content.match(/\b[A-ZÀ-Ú0-9]{2,}(?:[\s\-][A-ZÀ-Ú0-9]+)*\b/g);
            if (capPhrases) {
                capPhrases.forEach(phrase => {
                    const p = phrase.trim().toUpperCase();

                    // PRIORITY: Check Manual Overrides first
                    if (manualOverrides[p]) {
                        addToCategory(p, 'PROP', block.id);
                        overridesHandledThisBlock.add(p);
                        return;
                    }

                    // Priority: Check Learned Elements next
                    if (learnedElements[p]) {
                        addToCategory(p, 'PROP', block.id);
                        overridesHandledThisBlock.add(p);
                        return;
                    }

                    if (isSfxToken(p) || tempSfx[p]) return;
                    if (isGroupCharacter(p, block.content)) {
                        addToCategory(canonicalizeName(p), 'CHARACTER', block.id, { isIntroduced: true });
                        return;
                    }
                    if (p.length < 2 || SHARED_IGNORE_LIST.has(p) || knownCharacters.has(p) || knownLocations.has(p) || /^\d+$/.test(p)) return;
                    if (propVerbs.some(v => contentUpper.includes(v)) ||
                        blocks.filter(b => b.content.toUpperCase().includes(p)).length >= 2 ||
                        COMMON_INANIMATE_OBJECTS.includes(p)) {
                        addToCategory(p, 'PROP', block.id);
                    }
                });
            }

            // Asterisk-based tokens and General Manual Overrides / Learned Elements
            // We scan specifically for all known overrides and learned knowledge in this block's content
            const allKnownOverrides = { ...learnedElements, ...manualOverrides };
            Object.keys(allKnownOverrides).forEach(key => {
                if (key.length < 2) return;
                if (overridesHandledThisBlock.has(key)) return; // Skip if already handled

                const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const overrideRegex = new RegExp(`\\b${escapedKey}\\b`, 'i');
                if (overrideRegex.test(block.content)) {
                    addToCategory(key, allKnownOverrides[key], block.id);
                    overridesHandledThisBlock.add(key);
                }
            });

            // Specific asterisk-based tokens (keep as backup or for non-override cases if needed)
            const asteriskMatches = block.content.match(/\*([A-Za-zÀ-ÖØ-öø-ÿ0-9]+)/g);
            if (asteriskMatches) {
                asteriskMatches.forEach(am => {
                    const clean = am.replace(/\*/g, '').trim().toUpperCase();
                    if (manualOverrides[clean]) {
                        if (!overridesHandledThisBlock.has(clean)) {
                            addToCategory(clean, manualOverrides[clean], block.id);
                            overridesHandledThisBlock.add(clean);
                        }
                    }
                });
            }

            // NEW: Use strict extraction with confidence scoring
            const extractedLocations = extractLocationsFromAction(block.content);
            extractedLocations.forEach(loc => {
                const upLoc = loc.toUpperCase();
                // Check overrides for locations too (e.g. if re-classified as Character)
                if (manualOverrides[upLoc]) {
                    if (!overridesHandledThisBlock.has(upLoc)) {
                        addToCategory(upLoc, 'LOCATION', block.id);
                        overridesHandledThisBlock.add(upLoc);
                    }
                    return;
                }

                if (!SHARED_IGNORE_LIST.has(loc) && !knownCharacters.has(loc) && !COMMON_INANIMATE_OBJECTS.includes(loc)) {
                    // Check if it appears at least twice in the script or is a strong match? 
                    // The requirement from user "só aceitar LOCAÇÃO quando houver padrões fortes" is handled by the extractor.
                    // The previous logic checked for frequency (length >= 2). 
                    // Given the strictness of the new extractor (confidence score), we can trust it more.
                    // But for safety, "Validação final: ... ou evidência contextual".
                    // The extractor implements the evidence context.
                    addToCategory(loc, 'LOCATION', block.id);
                }
            });
        }

        if (block.type === 'action' || block.type === 'transition') {
            extractInlineSfx(block.content).forEach(sound => addToCategory(sound, 'SFX', block.id));
        }

        if (block.type === 'sfx') addToCategory(block.content.trim().toUpperCase(), 'SFX', block.id);
        if (block.type === 'camera-direction') {
            const cueLine = block.content.split('\n')[0].trim();
            const cueName = block.camera?.rawCue || cueLine;
            addToCategory(cueName.toUpperCase(), 'CAMERA', block.id);
        }
        if (block.type === 'transition') addToCategory(block.content.trim().toUpperCase(), 'TRANSITION', block.id);
    });

    // ENSURE ABSOLUTE PERSISTENCE: 
    // Any manual override must be in the final result, even if it has 0 occurrences in the current analysis.
    Object.keys(manualOverrides).forEach(key => {
        const cat = manualOverrides[key];
        if (cat === 'IGNORE') return;

        // Use normalized category (ensure SFX -> SFX_VFX consistency)
        const targetCat = cat === 'SFX' ? 'SFX_VFX' : cat;

        // Find if already exists in categories
        let exists = false;
        if (targetCat === 'CHARACTER') exists = !!tempChars[key];
        else if (targetCat === 'LOCATION') exists = !!tempLocs[key];
        else if (targetCat === 'PROP') exists = !!tempProps[key];
        else if (targetCat === 'SFX_VFX') exists = !!tempSfx[key];
        else if (targetCat === 'CAMERA') exists = !!tempCameras[key];
        else if (targetCat === 'TRANSITION') exists = !!tempTransitions[key];
        else exists = tempCustom[targetCat] && tempCustom[targetCat].some(item => item.name === key);

        if (!exists) {
            // Add with 0 count to ensure it appears in UI (Source of Truth)
            addToCategory(key, cat, "override", {}, false);
        }
    });

    return {
        scenes: tempScenes,
        characters: Object.values(tempChars).filter(c => c.dialogueCount > 0 || (c as any).isIntroduced || manualOverrides[c.name.toUpperCase()]).sort((a, b) => b.dialogueCount - a.dialogueCount),
        locations: Object.values(tempLocs).sort((a, b) => b.count - a.count),
        props: Object.values(tempProps).sort((a, b) => b.count - a.count),
        sfx: Object.values(tempSfx).sort((a, b) => b.count - a.count),
        cameras: Object.values(tempCameras).sort((a, b) => b.count - a.count),
        transitions: Object.values(tempTransitions).sort((a, b) => b.count - a.count),
        customCategories: tempCustom,
        totalPages: Math.ceil(runningCharCount / CHARS_PER_PAGE),
        estimatedPdfPages: Math.ceil(totalPhysicalLines / LINES_PER_PAGE) || 1,
        totalWords: totalStatsWordCount
    };
};

export const useScriptAnalysis = (
    blocks: ScriptBlock[],
    config: any,
    manualOverrides: Record<string, ElementCategory> = {},
    learnedElements: Record<string, string> = {}
) => {
    const [data, setData] = useState<AnalysisData>({
        scenes: [],
        characters: [],
        locations: [],
        props: [],
        sfx: [],
        cameras: [],
        transitions: [],
        totalPages: 0,
        estimatedPdfPages: 0,
        totalWords: 0
    });

    useEffect(() => {
        const handler = setTimeout(() => {
            const results = performScriptAnalysis(blocks, config, manualOverrides, learnedElements);
            setData(results);
        }, 500); // Reduced from 1500 to 500 for better responsiveness
        return () => clearTimeout(handler);
    }, [blocks, config, manualOverrides, learnedElements]);

    return data;
};
